/**
 * Filtered-results presentation for the add-players drawer (SE-6953, PC7).
 *
 * Top to bottom: the "N RESULTS · N FILTERS APPLIED" summary row with CLEAR
 * ALL on the right, the applied filters as removable chips, then the result
 * cards. Cards use the Suggested layout (identity, GPR, reason chips where
 * the player is a suggestion candidate) but carry **Add only — no Dismiss**;
 * rows already in this pipeline are muted "In list". Global mode pages by
 * scroll; the lists-scoped and reason-only modes are fully client-side.
 * @remarks View component — no business logic, hooks, or state.
 */

import { TEST_ID_ADD_PLAYERS_PANEL } from "e2e/testId";
import React, { memo } from "react";
import type { NativeScrollEvent, NativeSyntheticEvent } from "react-native";

import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import RemovablePill from "@/components/molecules/RemovablePill";
import type { SuggestionReasonCategory } from "@/generated/graphql";
import { ifWeb } from "@/utils/platform";

import AddPlayersRow from "../../molecules/AddPlayersRow";

/** Fallback for a chip whose removal handler is missing — renders inert. */
const NOOP = () => {};

/** One pre-formatted result card. */
export interface FilteredResultRow {
  /** Player id — the add target and the row's test identity. */
  readonly playerId: string;
  /** Display name. */
  readonly name: string;
  /** Pre-composed secondary line (club · age). */
  readonly meta: string;
  /** GPR score for the tone pill; `null` omits it. */
  readonly gpr: string | null;
  /** Reason categories, when the player is a suggestion candidate. */
  readonly reasons?: readonly SuggestionReasonCategory[];
  /** Whether the player already sits in this pipeline. */
  readonly isInList: boolean;
}

/** Props for {@link AddPlayersFilteredResultsView}. */
interface AddPlayersFilteredResultsViewProps {
  /** The result cards, in render order. */
  readonly rows: readonly FilteredResultRow[];
  /** Total result count for the summary row. */
  readonly resultCount: number;
  /** Applied filter count for the summary row. */
  readonly filtersAppliedCount: number;
  /** Applied-filter chip descriptors, in render order. */
  readonly chips: readonly { readonly key: string; readonly label: string }[];
  /** Pre-built removal handlers keyed by chip key. */
  readonly chipRemoveHandlers: Readonly<Record<string, () => void>>;
  /** Remove every applied filter, restoring the tabbed state. */
  readonly onClearAll: () => void;
  /** Whether the first page of server results is loading. */
  readonly isLoading: boolean;
  /** Results error, or `null`. */
  readonly error: string | null;
  /** Player ids with an add in flight. */
  readonly addingPlayerIds: readonly string[];
  /** Add a player, by id. */
  readonly onAdd: (playerId: string) => void;
  /**
   * Near-bottom scroll handler that loads the next global page, pre-built by
   * the container. Absent in the fully-client modes and when there is no
   * further page.
   */
  readonly onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
}

/**
 * Render the filtered-results body.
 * @param root0 - Component props.
 * @param root0.rows - The result cards.
 * @param root0.resultCount - Total result count.
 * @param root0.filtersAppliedCount - Applied filter count.
 * @param root0.chips - Applied-filter chip descriptors.
 * @param root0.chipRemoveHandlers - Per-chip removal callbacks.
 * @param root0.onClearAll - Remove every applied filter.
 * @param root0.isLoading - Whether results are loading.
 * @param root0.error - Results error.
 * @param root0.addingPlayerIds - Ids with an add in flight.
 * @param root0.onAdd - Add callback.
 * @param root0.onScroll - Near-bottom scroll handler for the next page.
 */
const AddPlayersFilteredResultsView = ({
  rows,
  resultCount,
  filtersAppliedCount,
  chips,
  chipRemoveHandlers,
  onClearAll,
  isLoading,
  error,
  addingPlayerIds,
  onAdd,
  onScroll,
}: AddPlayersFilteredResultsViewProps) => (
  <Stack
    testID={TEST_ID_ADD_PLAYERS_PANEL.FILTERED_RESULTS}
    className="min-h-0 flex-1 gap-3"
  >
    {/* Summary row: "N RESULTS · N FILTERS APPLIED" + CLEAR ALL. */}
    <Stack direction="horizontal" className="items-center justify-between">
      <Text
        variant="caption-strong"
        testID={TEST_ID_ADD_PLAYERS_PANEL.FILTERED_SUMMARY}
        className="text-content-secondary"
      >
        {`${resultCount} RESULTS · ${filtersAppliedCount} FILTERS APPLIED`}
      </Text>
      <Pressable
        testID={TEST_ID_ADD_PLAYERS_PANEL.FILTERED_CLEAR_ALL}
        onPress={onClearAll}
      >
        <Text variant="caption-strong" className="text-accent-primary">
          CLEAR ALL
        </Text>
      </Pressable>
    </Stack>

    {/* Removable applied-filter chips (RemovablePill — no hand-rolled pill
        chrome in a feature view). Removing one re-runs the results; removing
        the last restores the tab strip (the drawer derives its view from the
        applied set, so there is nothing extra to undo). */}
    {chips.length > 0 ? (
      <Stack direction="horizontal" className="flex-wrap gap-2">
        {chips.map(chip => (
          <RemovablePill
            key={chip.key}
            testID={TEST_ID_ADD_PLAYERS_PANEL.FILTERED_CHIP(chip.key)}
            label={chip.label}
            onRemove={chipRemoveHandlers[chip.key] ?? NOOP}
            removeAccessibilityLabel={`Remove filter ${chip.label}`}
          />
        ))}
      </Stack>
    ) : null}

    {isLoading ? (
      <Box className="flex-1 items-center justify-center p-4">
        <Spinner size="small" />
      </Box>
    ) : error ? (
      <Box className="flex-1 items-center justify-center p-4">
        <Text variant="caption" className="text-center text-status-error">
          {error}
        </Text>
      </Box>
    ) : rows.length === 0 ? (
      <Box className="flex-1 items-center justify-center p-4">
        <Text variant="caption" className="text-center text-content-muted">
          No players match these filters. Remove a filter, or CLEAR ALL to start
          over.
        </Text>
      </Box>
    ) : (
      <ScrollView
        className={`flex-1 ${ifWeb("scrollbar-hide", "")}`.trim()}
        showsVerticalScrollIndicator={false}
        onScroll={onScroll}
        scrollEventThrottle={400}
      >
        {/* Same column geometry as the Suggested list this replaces. */}
        <Stack className="gap-3 pb-2">
          {rows.map(row => (
            <AddPlayersRow
              key={row.playerId}
              playerId={row.playerId}
              name={row.name}
              meta={row.meta}
              gpr={row.gpr}
              reasons={row.reasons}
              isAdding={addingPlayerIds.includes(row.playerId)}
              isInList={row.isInList}
              onAdd={onAdd}
            />
          ))}
        </Stack>
      </ScrollView>
    )}
  </Stack>
);

AddPlayersFilteredResultsView.displayName = "AddPlayersFilteredResultsView";

export default memo(AddPlayersFilteredResultsView);
